import React from 'react';
import Select from '../../../components/ui/Select';
import { useTranslation } from 'react-i18next';
import Textarea from '../../../components/ui/Textarea';
import Button from '../../../components/ui/Button';

const ReviewForm = ({
  reviewForm,
  setReviewForm,
  criteriaAnswers,
  setCriteriaAnswers,
  reviewCriteria,
  submission,
  onSubmit,
  onSaveDraft,
  saving
}) => {
  const { t } = useTranslation('reviews');
  const handleScoreChange = (field, value) => {
    const numValue = parseInt(value);
    const maxValues = { overallScore: 10, confidenceLevel: 5 };
    const max = maxValues[field] || 10;
    if (numValue >= 1 && numValue <= max) {
      setReviewForm({ ...reviewForm, [field]: numValue });
    }
  };

  const handleCriteriaAnswerChange = (criteriaId, value) => {
    setCriteriaAnswers({
      ...criteriaAnswers,
      [criteriaId]: value
    });
  };

  const decisionOptions = [
    { value: 'accept', label: 'Accept' },
    { value: 'minor_revision', label: 'Minor Revision' },
    { value: 'major_revision', label: 'Major Revision' },
    { value: 'reject', label: 'Reject' },
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border">
        <h2 className="text-base font-semibold text-foreground">Review Form</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Complete your evaluation below. Fields marked with * are required.
        </p>
      </div>

      {/* Form Content */}
      <div className="p-5 space-y-5">
        {/* Overall Score */}
        <div className="space-y-2 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <label className="block text-sm font-semibold text-foreground">
            Overall Score * <span className="text-muted-foreground font-normal">(1-10)</span>
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="1"
              max="10"
              value={reviewForm?.overallScore}
              onChange={(e) => handleScoreChange('overallScore', e?.target?.value)}
              className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="w-12 h-10 bg-primary text-primary-foreground rounded-md flex items-center justify-center text-lg font-bold">
              {reviewForm?.overallScore}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            1 = Poor, 10 = Excellent
          </p>
        </div>

        {/* Custom Review Criteria */}
        {reviewCriteria?.map((criteria) => (
          <div key={criteria?.id} className="space-y-2 p-4 bg-muted/20 rounded-lg border border-border">
            <label className="block text-sm font-semibold text-foreground">
              {criteria?.questionText}
              {criteria?.isRequired && <span className="text-error ml-1">*</span>}
            </label>

            {criteria?.helpText && (
              <p className="text-xs text-muted-foreground">{criteria?.helpText}</p>
            )}

            {criteria?.questionType === 'scale' && (
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={criteria?.scaleMin || 1}
                  max={criteria?.scaleMax || 5}
                  value={criteriaAnswers?.[criteria?.id] || criteria?.scaleMin || 1}
                  onChange={(e) => handleCriteriaAnswerChange(criteria?.id, parseInt(e?.target?.value))}
                  className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <div className="w-12 h-10 bg-primary/10 text-primary rounded-md flex items-center justify-center text-lg font-bold">
                  {criteriaAnswers?.[criteria?.id] || criteria?.scaleMin || 1}
                </div>
              </div>
            )}

            {criteria?.questionType === 'short_text' && (
              <input
                type="text"
                value={criteriaAnswers?.[criteria?.id] || ''}
                onChange={(e) => handleCriteriaAnswerChange(criteria?.id, e?.target?.value)}
                maxLength={500}
                className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground"
                placeholder="Enter your response..."
              />
            )}

            {criteria?.questionType === 'long_text' && (
              <Textarea
                value={criteriaAnswers?.[criteria?.id] || ''}
                onChange={(e) => handleCriteriaAnswerChange(criteria?.id, e?.target?.value)}
                rows={4}
                placeholder={t('reviewForm.responsePlaceholder', 'Enter your response…')}
              />
            )}

            {criteria?.questionType === 'true_false' && (
              <div className="flex gap-3">
                <button
                  onClick={() => handleCriteriaAnswerChange(criteria?.id, true)}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    criteriaAnswers?.[criteria?.id] === true
                      ? 'bg-success text-success-foreground border-success'
                      : 'bg-card text-foreground border-border hover:bg-muted'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => handleCriteriaAnswerChange(criteria?.id, false)}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                    criteriaAnswers?.[criteria?.id] === false
                      ? 'bg-error text-error-foreground border-error'
                      : 'bg-card text-foreground border-border hover:bg-muted'
                  }`}
                >
                  No
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Detailed Feedback */}
        {/* These three carry the substance of a review, and all three were
            hand-rolled markup with no programmatic label association and no
            aria-describedby — a screen-reader user heard the heading and then
            an unlabelled box. */}
        <Textarea
          label={t('reviewForm.detailedFeedback', 'Detailed feedback')}
          required
          rows={5}
          value={reviewForm?.detailedFeedback || ''}
          onChange={(e) => setReviewForm({ ...reviewForm, detailedFeedback: e?.target?.value })}
          placeholder={t('reviewForm.detailedFeedbackPlaceholder', 'Provide comprehensive feedback on the submission…')}
        />

        {/* Strengths & Weaknesses side by side on larger screens */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Textarea
            label={t('reviewForm.strengths', 'Strengths')}
            rows={4}
            value={reviewForm?.strengths || ''}
            onChange={(e) => setReviewForm({ ...reviewForm, strengths: e?.target?.value })}
            placeholder={t('reviewForm.strengthsPlaceholder', 'What are the strong points of this work?')}
          />

          <Textarea
            label={t('reviewForm.weaknesses', 'Weaknesses')}
            rows={4}
            value={reviewForm?.weaknesses || ''}
            onChange={(e) => setReviewForm({ ...reviewForm, weaknesses: e?.target?.value })}
            placeholder={t('reviewForm.weaknessesPlaceholder', 'What areas need improvement?')}
          />
        </div>

        {/* Decision & Confidence side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-foreground">
              Recommendation *
            </label>
            <Select
              value={reviewForm?.decision}
              onChange={(value) => setReviewForm({ ...reviewForm, decision: value })}
              options={decisionOptions}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-foreground">
              Confidence Level * <span className="text-muted-foreground font-normal">(1-5)</span>
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="1"
                max="5"
                value={reviewForm?.confidenceLevel}
                onChange={(e) => handleScoreChange('confidenceLevel', e?.target?.value)}
                className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-secondary"
              />
              <div className="w-12 h-10 bg-secondary text-secondary-foreground rounded-md flex items-center justify-center text-lg font-bold">
                {reviewForm?.confidenceLevel}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              1 = Low, 5 = Very High
            </p>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="px-5 py-3 border-t border-border bg-muted/20 flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          size="default"
          onClick={onSaveDraft}
          disabled={saving}
          iconName="Save"
          iconPosition="left"
          className="flex-1"
        >
          Save Draft
        </Button>
        <Button
          variant="default"
          size="default"
          onClick={onSubmit}
          disabled={saving}
          loading={saving}
          iconName="Send"
          iconPosition="left"
          className="flex-1"
        >
          Submit Review
        </Button>
      </div>
    </div>
  );
};

export default ReviewForm;
